import type { ReactNode } from 'react';
import { Box, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import BookmarksOutlinedIcon from '@mui/icons-material/BookmarksOutlined';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import { LiveDot } from '../../../shared/components/LiveDot';
import { SEMANTIC_COLOR_VARS, hashSemanticColor } from '../../../shared/components/semanticColor';
import { formatEntityTypeLabel } from '../../../shared/components/entityTypeLabel';
import { formatRelativeTime } from '../../../shared/components/relativeTime';
import type { Entity, EntityTypeSkill, EntityTypeEnrichment } from '../../../core/types/data-models';

/** Rows a count's hover popup lists before it ends with a "+N more" line. */
const POPUP_LIST_LIMIT = 12;

/** Clickable line inside a count popup (an entity, an analysis). */
const popupRowSx = {
  display: 'block',
  lineHeight: 1.6,
  cursor: 'pointer',
  '&:hover': { textDecoration: 'underline' },
};

interface StatPopupProps {
  title: string;
  /** Shown instead of the rows when the count is 0. */
  empty: string;
  /** Full count, so the popup can report what its `POPUP_LIST_LIMIT` cut hides. */
  count: number;
  children: ReactNode;
}

function StatPopup({ title, empty, count, children }: StatPopupProps) {
  const hidden = count - POPUP_LIST_LIMIT;
  return (
    <Box sx={{ maxHeight: 260, overflowY: 'auto' }}>
      <Typography variant="caption" fontWeight={700} display="block" sx={{ mb: 0.5 }}>
        {title}
      </Typography>
      {count === 0 ? (
        <Typography variant="caption" display="block" sx={{ fontStyle: 'italic' }}>
          {empty}
        </Typography>
      ) : (
        <>
          {children}
          {hidden > 0 && (
            <Typography variant="caption" display="block" sx={{ mt: 0.5, fontStyle: 'italic' }}>
              +{hidden} more
            </Typography>
          )}
        </>
      )}
    </Box>
  );
}

interface CardStatProps {
  value: ReactNode;
  label: string;
  /** The type's accent color — the count is drawn in it. */
  color: string;
  popup: ReactNode;
}

/** One hoverable count in a type card's footer row. */
function CardStat({ value, label, color, popup }: CardStatProps) {
  return (
    <Tooltip title={popup} placement="bottom">
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          py: 0.5,
          textAlign: 'center',
          borderRadius: 'var(--radius-sm)',
          transition: 'background-color var(--transition)',
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        <Typography sx={{ fontFamily: 'var(--font-mono)', fontSize: 17, fontWeight: 700, lineHeight: 1.2, color }}>
          {value}
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" noWrap sx={{ fontSize: 10 }}>
          {label}
        </Typography>
      </Box>
    </Tooltip>
  );
}

interface ManagedTypeCardProps {
  entityType: string;
  entities: Entity[];
  /** Insight topics already generated for this type — one per EntityTypeSkill run. */
  topics: EntityTypeEnrichment[];
  skills: EntityTypeSkill[];
  /** One of this type's skills is being run right now. */
  running: boolean;
  /** Open the type's ManagedEntityTypeDetail view, optionally straight onto a `?tab=`. */
  onOpen: (tab?: string) => void;
  onOpenEntity: (entity: Entity) => void;
}

/**
 * One compact card per ProjectManagedEntityType row (the sidebar's MANAGED
 * KNOWLEDGE list), or per extracted type on the validation summary: the
 * type's name plus two counts — its entities, and the analyses run so far
 * out of the skills the type has. Each count lists what it
 * counts in a hover popup, so the card face stays a glanceable summary. The
 * card, its arrow, and the popup rows link into ManagedEntityTypeDetail — that
 * view is also where a skill is run — and a topic row opens it on its own tab.
 */
export function ManagedTypeCard({ entityType, entities, topics, skills, running, onOpen, onOpenEntity }: ManagedTypeCardProps) {
  const vars = SEMANTIC_COLOR_VARS[hashSemanticColor(entityType)];
  const label = formatEntityTypeLabel(entityType);

  // Managed (user-pinned) entities first, so the popup's cut never hides one.
  const sortedEntities = [...entities].sort(
    (a, b) => Number(b.isManaged) - Number(a.isManaged) || a.entityName.localeCompare(b.entityName),
  );
  const sortedTopics = [...topics].sort(
    (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
  );
  const ranSkillIds = new Set(topics.map((t) => t.skillId));
  // Performed analyses first, then the skills still to run — the two halves of
  // the "N/M Analyses" count, in one popup list.
  const analysisRows = [
    ...sortedTopics.map((topic) => ({
      key: `topic-${topic.id}`,
      done: true,
      name: topic.topicTitle,
      meta: formatRelativeTime(topic.updatedAt),
      onClick: () => onOpen(String(topic.id)),
    })),
    ...skills
      .filter((skill) => !ranSkillIds.has(skill.id))
      .map((skill) => ({
        key: `skill-${skill.id}`,
        done: false,
        name: skill.name,
        meta: undefined as string | undefined,
        onClick: undefined,
      })),
  ];

  return (
    <Box
      onClick={() => onOpen()}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
        bgcolor: vars.soft,
        border: '1px solid',
        borderColor: vars.line,
        borderRadius: 'var(--radius-md)',
        cursor: 'pointer',
        transition: 'filter var(--transition)',
        // The tint is a CSS variable, so it can't be alpha-blended here —
        // a brightness shift gives the hover cue in either theme.
        '&:hover': {
          filter: (theme) => (theme.palette.mode === 'dark' ? 'brightness(1.15)' : 'brightness(0.96)'),
        },
      }}
    >
      {/* Title row — the sidebar's MANAGED KNOWLEDGE row (bookmark + type name). */}
      <Stack direction="row" alignItems="center" spacing={0.75} sx={{ px: 1.25, pt: 1 }}>
        <BookmarksOutlinedIcon sx={{ fontSize: 15, color: vars.main, flexShrink: 0 }} />
        <Typography
          variant="body2"
          fontWeight={600}
          sx={{ flex: 1, minWidth: 0, fontSize: 13, color: vars.main }}
          noWrap
        >
          {label}
        </Typography>
        {running && <LiveDot size={7} />}
        <Tooltip title={`Open ${label} knowledge view`}>
          <IconButton
            size="small"
            aria-label={`Open ${label} knowledge view`}
            onClick={(e) => {
              e.stopPropagation();
              onOpen();
            }}
            sx={{ p: 0.25, color: vars.main }}
          >
            <ArrowForwardIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      </Stack>

      {/* Counts — each lists what it counts on hover. */}
      <Stack direction="row" sx={{ px: 0.75, pt: 0.25, pb: 0.75 }}>
        <CardStat
          value={entities.length}
          label="Entities"
          color={vars.main}
          popup={
            <StatPopup title={`${label} entities`} empty="No entities extracted yet" count={sortedEntities.length}>
              {sortedEntities.slice(0, POPUP_LIST_LIMIT).map((e) => (
                <Typography
                  key={e.id}
                  variant="caption"
                  onClick={() => onOpenEntity(e)}
                  sx={[popupRowSx, e.isManaged && { fontWeight: 700 }]}
                >
                  {e.entityName}
                </Typography>
              ))}
            </StatPopup>
          }
        />
        <CardStat
          value={`${topics.length}/${skills.length}`}
          label="Analyses"
          color={vars.main}
          popup={
            <StatPopup
              title={`${label} analyses`}
              empty="No skills defined for this type"
              count={analysisRows.length}
            >
              {analysisRows.slice(0, POPUP_LIST_LIMIT).map((analysis) => (
                <Stack key={analysis.key} direction="row" alignItems="center" spacing={0.5}>
                  {analysis.done ? (
                    <CheckRoundedIcon sx={{ fontSize: 12, flexShrink: 0 }} />
                  ) : (
                    <Box sx={{ width: 12, flexShrink: 0 }} />
                  )}
                  <Typography
                    variant="caption"
                    onClick={analysis.onClick}
                    sx={[
                      { lineHeight: 1.6, opacity: analysis.done ? 1 : 0.75 },
                      analysis.onClick != null && { cursor: 'pointer', '&:hover': { textDecoration: 'underline' } },
                    ]}
                  >
                    {analysis.name}
                    {analysis.meta && (
                      <Box component="span" sx={{ opacity: 0.7 }}>
                        {' · '}
                        {analysis.meta}
                      </Box>
                    )}
                  </Typography>
                </Stack>
              ))}
            </StatPopup>
          }
        />
      </Stack>
    </Box>
  );
}
